// In src/pages/EventRegistrations.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, doc, onSnapshot, updateDoc, query, orderBy, writeBatch } from 'firebase/firestore'; // writeBatch is already imported

import { 
  Container, Typography, CircularProgress, Box, Button, Paper,
  Switch, Alert, Divider, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow 
} from '@mui/material'; 
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import PaidIcon from '@mui/icons-material/Paid'; 
import PendingIcon from '@mui/icons-material/HourglassEmpty'; 


function EventRegistrations() {
  const { eventId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCurrentUserPOC, setIsCurrentUserPOC] = useState(false); 

  const isAdmin = userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';

  // All Hooks must be called unconditionally at the top level of the component
  useEffect(() => {
    if (!userProfile?.churchId || !eventId) return;

    const eventDocRef = doc(db, 'churches', userProfile.churchId, 'events', eventId);
    const unsubscribe = onSnapshot(eventDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const eventData = { id: docSnap.id, ...docSnap.data() };
        setEvent(eventData);
        if (currentUser && eventData.eventPOCs?.includes(currentUser.uid)) {
          setIsCurrentUserPOC(true);
        } else {
          setIsCurrentUserPOC(false);
        }
      } else {
        setError('Event not found.');
      }
    });
    return () => unsubscribe();
  }, [eventId, userProfile, currentUser]); 

  useEffect(() => {
    if (!userProfile?.churchId || !eventId) return;

    const registrationsColRef = collection(db, 'churches', userProfile.churchId, 'events', eventId, 'registrations');
    const q = query(registrationsColRef, orderBy('registeredAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedRegistrations = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRegistrations(fetchedRegistrations);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching registrations:", err);
      setError('You do not have permission to view these registrations.');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [eventId, userProfile]);

  const handleCheckInToggle = useCallback(async (registrationId, currentStatus) => {
    if (!userProfile?.churchId || !eventId) return;
    const regDocRef = doc(db, 'churches', userProfile.churchId, 'events', eventId, 'registrations', registrationId);
    try {
      await updateDoc(regDocRef, {
        attended: !currentStatus
      });
    } catch (err) {
      console.error("Error updating attendance:", err);
      alert("Failed to update check-in status.");
    }
  }, [eventId, userProfile]);
  
  const handlePaymentStatusToggle = useCallback(async (registrationId, currentStatus) => {
    if (!userProfile?.churchId || !eventId) return;
    const regDocRef = doc(db, 'churches', userProfile.churchId, 'events', eventId, 'registrations', registrationId);
    const newPaymentStatus = currentStatus === 'paid' ? 'pending' : 'paid';
    try {
      await updateDoc(regDocRef, {
        paymentStatus: newPaymentStatus
      });
    } catch (err) {
      console.error("Error updating payment status:", err);
      alert("Failed to update payment status.");
    }
  }, [eventId, userProfile]);

  const handleExportCSV = useCallback(() => {
    if (registrations.length === 0) return;
    
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Name,Registered At,Payment Status,Attended\r\n";
    
    registrations.forEach(reg => {
      const name = reg.userName.replace(/,/g, ''); 
      const date = reg.registeredAt?.toDate().toLocaleString() || 'N/A';
      const status = reg.paymentStatus || 'N/A';
      const attended = reg.attended ? 'Yes' : 'No';
      csvContent += `${name},${date},${status},${attended}\r\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${event?.title || 'event'}_registrations.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [event, registrations]); 
  
  const handleCancelRegistration = useCallback(async (regIdToCancel) => {
    console.log("handleCancelRegistration called for regId:", regIdToCancel); 

    if (!userProfile?.churchId || !eventId) {
      console.error("handleCancelRegistration: Missing churchId or eventId."); 
      return;
    }

    const registrationToCancel = registrations.find(reg => reg.id === regIdToCancel);

    if (!registrationToCancel) { 
        console.error("handleCancelRegistration: Registration not found in local state for ID:", regIdToCancel);
        alert("Failed to cancel registration: Registration data not found.");
        return;
    }

    if (registrationToCancel.paymentStatus === 'paid') {
      alert("Cannot cancel registration: This member has already paid for the event.");
      return;
    }

    if (window.confirm("Are you sure you want to cancel this registration?")) {
      try {
        console.log("Attempting to delete registration and update event count..."); 
        const batch = writeBatch(db); // Corrected: use writeBatch(db)
        const eventDocRef = doc(db, 'churches', userProfile.churchId, 'events', eventId);
        const registrationDocRef = doc(db, 'churches', userProfile.churchId, 'events', eventId, 'registrations', regIdToCancel);
        
        batch.delete(registrationDocRef);
        batch.update(eventDocRef, { currentRegistrations: event.currentRegistrations - 1 });

        await batch.commit();
        console.log("Batch commit successful."); 
        alert("Registration cancelled successfully.");
      } catch (err) {
        console.error("Error cancelling registration during Firebase operation:", err); 
        alert("Failed to cancel registration: " + err.message); 
      }
    } else {
        console.log("Cancellation aborted by user."); 
    }
  }, [eventId, userProfile, event, registrations]); 


  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  const attendedCount = registrations.filter(r => r.attended).length;
  const paidCount = registrations.filter(r => r.paymentStatus === 'paid').length; 

  const canManageRegistrations = isAdmin || isCurrentUserPOC;

  // The conditional return must be after all hooks are called
  if (!canManageRegistrations) {
    return (
      <Container maxWidth="md">
        <Typography variant="h6" color="error" sx={{ mt: 3 }}>
          You do not have permission to manage registrations for this event.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)} 
        sx={{ mb: 2 }}
      >
        Back to Event
      </Button>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h4" gutterBottom>{event?.title}</Typography>
        <Typography variant="h6" color="text.secondary">Registration Management</Typography>
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body1">
                <strong>Total Registered:</strong> {registrations.length} / {event?.capacity || 'N/A'}
            </Typography>
            <Typography variant="body1">
                <strong>Checked In:</strong> {attendedCount}
            </Typography>
            {event?.isPaidEvent && ( 
              <Typography variant="body1">
                  <strong>Paid:</strong> {paidCount}
              </Typography>
            )}
            <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                disabled={registrations.length === 0}
            >
                Export CSV
            </Button>
        </Box>

        <TableContainer component={Paper} elevation={0}>
          <Table aria-label="registrations table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Registered At</TableCell>
                {event?.isPaidEvent && <TableCell>Payment Status</TableCell>} 
                <TableCell>Attended</TableCell>
                <TableCell>Actions</TableCell> 
              </TableRow>
            </TableHead>
            <TableBody>
              {registrations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={event?.isPaidEvent ? 5 : 4} align="center">No registrations yet.</TableCell> 
                </TableRow>
              ) : (
                registrations.map((reg) => (
                  <TableRow key={reg.id}>
                    <TableCell>
                        <Box sx={{display: 'flex', flexDirection: 'column'}}>
                            <Typography variant="body1">{reg.userName}</Typography>
                            {event?.isPaidEvent && (
                                <Typography variant="caption" color="text.secondary">
                                    Price: ${reg.ticketPrice?.toFixed(2) || '0.00'}
                                </Typography>
                            )}
                        </Box>
                    </TableCell>
                    <TableCell>{reg.registeredAt?.toDate().toLocaleDateString()}</TableCell>
                    {event?.isPaidEvent && (
                      <TableCell>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Chip
                                label={reg.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                                color={reg.paymentStatus === 'paid' ? 'success' : 'warning'}
                                size="small"
                                icon={reg.paymentStatus === 'paid' ? <PaidIcon /> : <PendingIcon />}
                                sx={{ mr: 1 }}
                            />
                            <Switch
                                edge="end"
                                onChange={() => handlePaymentStatusToggle(reg.id, reg.paymentStatus)}
                                checked={reg.paymentStatus === 'paid'}
                                inputProps={{ 'aria-label': 'toggle payment status' }}
                                disabled={!canManageRegistrations} 
                            />
                        </Box>
                      </TableCell>
                    )}
                    <TableCell>
                      <Switch
                        edge="end"
                        onChange={() => handleCheckInToggle(reg.id, reg.attended)}
                        checked={reg.attended || false}
                        inputProps={{ 'aria-label': 'toggle attendance' }}
                        disabled={!canManageRegistrations} 
                      />
                    </TableCell>
                    <TableCell>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleCancelRegistration(reg.id)}
                            disabled={reg.paymentStatus === 'paid'} 
                        >
                            Cancel
                        </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
}

export default EventRegistrations;